/**
 * Supabase client — configured with custom JWT from Edge Function auth.
 * The access token is injected via custom fetch so auth.uid() works in RLS.
 *
 * AUTO-REFRESH: On 401 response, automatically refreshes the token
 * and retries the request once. This prevents "JWT expired" errors
 * when the user keeps the app open longer than the 1-hour token TTL.
 */

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set')
}

/** Mutable token ref — updated after auth */
let _accessToken: string | null = localStorage.getItem('turnifly_access_token')

/** Prevents multiple concurrent refresh attempts */
let _refreshPromise: Promise<boolean> | null = null

export function setSupabaseAccessToken(token: string | null): void {
  _accessToken = token
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns true if refresh succeeded, false otherwise.
 * Deduplicates concurrent refresh attempts.
 */
async function tryRefreshToken(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem('turnifly_refresh_token')
      if (!refreshTokenValue) return false

      const apiBase = `${supabaseUrl}/functions/v1/api/v1`
      const res = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      })

      if (!res.ok) return false

      const data = await res.json() as {
        access_token: string
        refresh_token: string
      }

      // Update everywhere
      _accessToken = data.access_token
      localStorage.setItem('turnifly_access_token', data.access_token)
      localStorage.setItem('turnifly_refresh_token', data.refresh_token)

      return true
    } catch {
      return false
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

/**
 * Custom fetch that auto-refreshes JWT on 401 and retries once.
 */
async function fetchWithAutoRefresh(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers)
  const token = _accessToken ?? localStorage.getItem('turnifly_access_token')
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(input, { ...init, headers })

  // If 401 and we have a refresh token, try to refresh and retry
  if (response.status === 401 && localStorage.getItem('turnifly_refresh_token')) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      // Retry with new token
      const retryHeaders = new Headers(init?.headers)
      retryHeaders.set('Authorization', `Bearer ${_accessToken}`)
      return fetch(input, { ...init, headers: retryHeaders })
    }
  }

  return response
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    fetch: fetchWithAutoRefresh,
  },
  auth: {
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
  },
})
